import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import JsonView from '@uiw/react-json-view';
import { ExternalLink } from 'lucide-react';
import { useMemo, useState, type CSSProperties, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import {
  evalResultFingerprint,
  evalResultQuestions,
  evalResultSummary,
  evalRunButtonLabel,
  evalRunButtonState,
  formatEvalCost,
  formatEvalScore,
  isAgentEvalWaiting,
  openEvalResultWindow,
  type PendingEvalWait,
} from './eval-result-view';
import {
  useAgentEvalOptions,
  useSavedAgentEvals,
  useStartAgentEval,
} from './useAgentConfigs';

import type {
  AgentEvalRunResult,
  SavedAgentEvalRun,
  StartAgentEvalResponse,
} from './agent-config.types';

export function AgentConfigEvalPanel({
  baseAgent,
  compareAgent,
  jsonStyle,
}: {
  baseAgent: string;
  compareAgent?: string;
  jsonStyle: CSSProperties;
}) {
  const search = useSearch({ from: '/agent-configs' });
  const navigate = useNavigate({ from: '/agent-configs' });
  const queryClient = useQueryClient();
  const optionsQuery = useAgentEvalOptions();
  const startEval = useStartAgentEval();
  const [result, setResult] = useState<StartAgentEvalResponse | null>(null);
  const [pending, setPending] = useState<PendingEvalWait | null>(null);

  const metric = search.metric;
  const maxQuestions = search.max_questions;
  const agents = [baseAgent, compareAgent].filter((name): name is string =>
    Boolean(name)
  );
  const metrics = optionsQuery.data?.metrics ?? [];

  const savedQuery = useMemo(
    () =>
      metric && baseAgent
        ? {
            metric,
            agents: [baseAgent, compareAgent].filter((name): name is string =>
              Boolean(name)
            ),
            max_questions: maxQuestions,
          }
        : null,
    [metric, baseAgent, compareAgent, maxQuestions]
  );
  const pendingForQuery =
    pending != null &&
    pending.metric === metric &&
    pending.max_questions === maxQuestions
      ? pending
      : null;
  const savedEvals = useSavedAgentEvals(savedQuery, pendingForQuery);
  const buttonState = evalRunButtonState({
    isStarting: startEval.isPending,
    selectedAgents: agents,
    runs: savedEvals.data?.runs,
    pending: pendingForQuery,
  });
  const canRun =
    Boolean(metric) &&
    agents.length > 0 &&
    !savedEvals.isLoading &&
    (buttonState === 'run' ||
      buttonState === 'run_missing' ||
      buttonState === 'run_again');

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canRun) return;
    try {
      const previousFingerprintByAgent = Object.fromEntries(
        (savedEvals.data?.runs ?? []).map((run) => [
          run.agent,
          evalResultFingerprint(run.results),
        ])
      );
      const response = await startEval.mutateAsync({
        metric,
        agents,
        max_questions: maxQuestions,
        rerun: buttonState === 'run_again',
      });
      setResult(response);
      setPending({
        metric,
        max_questions: maxQuestions,
        agents: response.runs
          .filter((run) => run.status === 'scheduled')
          .map((run) => run.agent),
        previousFingerprintByAgent,
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'agent-configs', 'evals', 'saved'],
      });
    } catch {
      // useStartAgentEval surfaces the message on the form
    }
  };

  return (
    <>
      <section className="shrink-0 rounded-lg border bg-card p-4">
        <div className="mb-3">
          <h3 className="text-base font-semibold">Eval</h3>
          <p className="text-sm text-muted-foreground">
            Runs against{' '}
            {agents.length === 2
              ? 'both selected agents'
              : 'the selected agent'}
            . Results are stored on S3. We email you a link to this page when a
            new run finishes.
          </p>
        </div>
        <form className="flex flex-col gap-3" onSubmit={onSubmit}>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Metric</span>
              <Select
                value={metric}
                onChange={(event) =>
                  navigate({
                    search: { ...search, metric: event.target.value },
                    replace: true,
                  })
                }
                required
              >
                <option value="">Select metric…</option>
                {metrics.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Max questions</span>
              <Input
                type="number"
                min={0}
                value={maxQuestions}
                onChange={(event) =>
                  navigate({
                    search: {
                      ...search,
                      max_questions: Number(event.target.value),
                    },
                    replace: true,
                  })
                }
              />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Button type="submit" disabled={!canRun}>
              {evalRunButtonLabel(buttonState)}
            </Button>
          </div>
        </form>
        {startEval.isError && (
          <p className="mt-3 text-sm text-destructive">
            {startEval.error instanceof Error
              ? startEval.error.message
              : 'Failed to start eval'}
          </p>
        )}
      </section>
      {metric ? (
        <EvalResultColumns
          baseAgent={baseAgent}
          compareAgent={compareAgent}
          isLoading={savedEvals.isLoading}
          isError={savedEvals.isError}
          error={savedEvals.error}
          savedRuns={savedEvals.data?.runs}
          submitResult={result}
          pending={pendingForQuery}
          jsonStyle={jsonStyle}
        />
      ) : null}
    </>
  );
}

function EvalResultColumns({
  baseAgent,
  compareAgent,
  isLoading,
  isError,
  error,
  savedRuns,
  submitResult,
  pending,
  jsonStyle,
}: {
  baseAgent: string;
  compareAgent?: string;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  savedRuns: SavedAgentEvalRun[] | undefined;
  submitResult: StartAgentEvalResponse | null;
  pending: PendingEvalWait | null;
  jsonStyle: CSSProperties;
}) {
  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">Checking saved results…</p>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        {error instanceof Error
          ? error.message
          : 'Failed to load saved results'}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <EvalResultColumn
        agent={baseAgent}
        savedRun={savedRuns?.find((run) => run.agent === baseAgent)}
        submitRun={submitResult?.runs.find((run) => run.agent === baseAgent)}
        isWaiting={isAgentEvalWaiting(
          pending,
          baseAgent,
          savedRuns?.find((run) => run.agent === baseAgent)?.results
        )}
        initiatorEmail={submitResult?.initiator_email}
        jsonStyle={jsonStyle}
      />
      {compareAgent ? (
        <EvalResultColumn
          agent={compareAgent}
          savedRun={savedRuns?.find((run) => run.agent === compareAgent)}
          submitRun={submitResult?.runs.find(
            (run) => run.agent === compareAgent
          )}
          isWaiting={isAgentEvalWaiting(
            pending,
            compareAgent,
            savedRuns?.find((run) => run.agent === compareAgent)?.results
          )}
          initiatorEmail={submitResult?.initiator_email}
          jsonStyle={jsonStyle}
        />
      ) : (
        <div />
      )}
    </div>
  );
}

function EvalResultColumn({
  agent,
  savedRun,
  submitRun,
  isWaiting,
  initiatorEmail,
  jsonStyle,
}: {
  agent: string;
  savedRun: SavedAgentEvalRun | undefined;
  submitRun: AgentEvalRunResult | undefined;
  isWaiting: boolean;
  initiatorEmail: string | undefined;
  jsonStyle: CSSProperties;
}) {
  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {agent}
      </div>
      {isWaiting ? (
        <p className="text-sm">
          {savedRun?.results != null ? 'Rerun scheduled' : 'Scheduled'}
          {submitRun?.workflow_id ? ` (${submitRun.workflow_id})` : ''}.
          Checking every 20s.
          {initiatorEmail
            ? ` We'll email ${initiatorEmail} a link to this page when it is ready.`
            : ''}
        </p>
      ) : null}
      {savedRun?.results == null ? (
        isWaiting ? null : (
          <p className="text-sm text-muted-foreground">
            Never run for this config
          </p>
        )
      ) : (
        <EvalSavedResult results={savedRun.results} jsonStyle={jsonStyle} />
      )}
    </section>
  );
}

function EvalSavedResult({
  results,
  jsonStyle,
}: {
  results: unknown;
  jsonStyle: CSSProperties;
}) {
  const summary = evalResultSummary(results);
  const questions = evalResultQuestions(results);
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-muted-foreground">total_score</dt>
          <dd className="text-2xl font-semibold tabular-nums text-amber-600 dark:text-amber-400">
            {formatEvalScore(summary.totalScore)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">mean_cost_usd_per_question</dt>
          <dd className="font-medium tabular-nums">
            {formatEvalCost(summary.meanCostUsdPerQuestion)}
          </dd>
        </div>
      </dl>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm underline underline-offset-2"
        onClick={() => openEvalResultWindow(results)}
      >
        Open full report
        <ExternalLink className="h-3.5 w-3.5" />
      </button>
      {questions.length > 0 ? (
        <Table containerClassName="max-h-80 rounded-md border">
          <TableHeader>
            <TableRow>
              <TableHead>Question</TableHead>
              <TableHead>Answer</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {questions.map((row, index) => (
              <TableRow key={`${index}-${row.question.slice(0, 24)}`}>
                <TableCell className="align-top whitespace-pre-wrap">
                  {row.question}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap">
                  {row.answer}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      <div className="max-h-80 overflow-auto rounded-md border bg-background p-3">
        <JsonView
          value={results as object}
          collapsed={1}
          displayDataTypes={false}
          displayObjectSize={false}
          style={jsonStyle}
        />
      </div>
    </div>
  );
}
